import { type CountryCode } from '@constants'
import { useApiEcCartRequestCache } from '@hooks-api/eCommerceV2/useApiEcCartRequestCache'
import { type ECommerceCartCalculateRequest } from '@hooks-api/types/apiRequest'
import { useMemo } from 'react'
import { create } from 'zustand'
import { type WpCartValues } from '../types'
import { useWpCartService } from './useWpCartService'

const useWpSyncCartSessionStore = create<{
  hasFinishedSyncCartSession: boolean
  setHasFinishedSyncCartSession: (value: boolean) => void
}>()(set => ({
  hasFinishedSyncCartSession: false,
  setHasFinishedSyncCartSession: (value: boolean) => set({ hasFinishedSyncCartSession: value }),
}))

interface UseWpSyncCartSessionToStoreProps {
  isXPlatformInfoLoading: boolean
  isUserLoggedIn: boolean
  countryCode: CountryCode
}

export const useWpSyncCartSessionToStore = (args: UseWpSyncCartSessionToStoreProps) => {
  const { isXPlatformInfoLoading, isUserLoggedIn, countryCode } = args
  const { syncCartStoreFromSession } = useWpCartService(countryCode)
  const { hasFinishedSyncCartSession, setHasFinishedSyncCartSession } = useWpSyncCartSessionStore()

  useApiEcCartRequestCache({ shouldRequest: isUserLoggedIn, country_code: countryCode }, {
    shouldRetryOnError: false,
    onError: () => {
      setHasFinishedSyncCartSession(true)
    },
    onSuccess: (data) => {
      const cache = data.data.data as ECommerceCartCalculateRequest<WpCartValues> | null
      syncCartStoreFromSession({
        cartValues: cache?.cart_values,
        orderItems: cache?.order_items,
        callback: () => {
          setHasFinishedSyncCartSession(true)
        },
      })
    },
  })

  const hasSyncCartSession = useMemo(() => {
    if (isXPlatformInfoLoading) return false
    if (isUserLoggedIn) return hasFinishedSyncCartSession
    return true
  }, [isXPlatformInfoLoading, isUserLoggedIn, hasFinishedSyncCartSession])

  return { hasSyncCartSession }
}
